import React from "react";
import styled from "styled-components";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

export const PastoralPagination = (props) => {
  const currentPage = props.currentPage;
  const itemsPerPage = props.itemsPerPage;
  const total_count = props.total_count;
  const handlePageChange = props.handlePageChange;

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(total_count / itemsPerPage));

  // Ensure current page is within bounds
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  // Calculate showing range
  const startIndex = (safeCurrentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(safeCurrentPage * itemsPerPage, total_count);

  // Navigation functions
  const prevPage = () => {
    if (safeCurrentPage > 1) {
      handlePageChange(safeCurrentPage - 1);
    }
  };

  const nextPage = () => {
    if (safeCurrentPage < totalPages) {
      handlePageChange(safeCurrentPage + 1);
    }
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      handlePageChange(pageNumber);
    }
  };

  return (
    <PaginationSection className="gap-2">
      <PaginationText>
        {total_count > 0
          ? `Showing ${startIndex}-${endIndex} of ${total_count} entries`
          : "No entries to display"}
      </PaginationText>

      {totalPages > 0 && (
        <PaginationControls>
          {/* Previous Button */}
          <button
            onClick={prevPage}
            disabled={safeCurrentPage === 1}
            className={`pagination-button ${
              safeCurrentPage === 1 ? "disabled" : ""
            }`}
          >
            <CaretLeft size={20} />
          </button>

          {/* Page Numbers */}
          {totalPages <= 7 ? (
            // If 7 or fewer pages, show all
            [...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={`pagination-number ${
                  safeCurrentPage === index + 1 ? "active" : ""
                }`}
              >
                {index + 1}
              </button>
            ))
          ) : (
            // If more than 7 pages, show a subset with ellipsis
            <>
              {/* Always show first page */}
              <button
                onClick={() => paginate(1)}
                className={`pagination-number ${
                  safeCurrentPage === 1 ? "active" : ""
                }`}
              >
                1
              </button>

              {/* Show ellipsis if not near the start */}
              {safeCurrentPage > 3 && (
                <span className="pagination-ellipsis">...</span>
              )}

              {/* Show pages around current page */}
              {[...Array(5)].map((_, index) => {
                const pageNum = Math.max(2, safeCurrentPage - 2) + index;
                // Only render if within range and not first/last page
                if (pageNum > 1 && pageNum < totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`pagination-number ${
                        safeCurrentPage === pageNum ? "active" : ""
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}

              {/* Show ellipsis if not near the end */}
              {safeCurrentPage < totalPages - 2 && (
                <span className="pagination-ellipsis">...</span>
              )}

              {/* Always show last page */}
              <button
                onClick={() => paginate(totalPages)}
                className={`pagination-number ${
                  safeCurrentPage === totalPages ? "active" : ""
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          {/* Next Button */}
          <button
            onClick={nextPage}
            disabled={safeCurrentPage === totalPages}
            className={`pagination-button ${
              safeCurrentPage === totalPages ? "disabled" : ""
            }`}
          >
            <CaretRight size={20} />
          </button>
        </PaginationControls>
      )}
    </PaginationSection>
  );
};

export default PastoralPagination;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;

  .pagination-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: #f1f1f1;
    color: #178b96;
    cursor: pointer;
    border: none;
    font-family: inherit;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
      background-color: #bfe9ee;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background-color: #f1f1f1;
      }
    }
  }

  .pagination-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: #f1f1f1;
    color: #178b96;
    cursor: pointer;
    border: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
      background-color: #bfe9ee;
    }

    &.active {
      background-color: #178b96;
      color: white;
    }
  }

  .pagination-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: #178b96;
    font-size: 14px;
  }
`;

const PaginationText = styled.span`
  color: #ff7234;
  text-align: center;
  font-size: 16px;
  font-family: "Futura PT", sans-serif;
  letter-spacing: 0.1em;
  font-weight: 400;
  line-height: normal;
`;

const PaginationSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0;
`;
